import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import { 
  Hammer, Zap, Shield, Globe, Heart, Users, 
  Code2, Type, Search, Sparkles, ArrowRight,
  CheckCircle2, Mail, MessageSquare
} from 'lucide-react';
import { motion } from 'framer-motion';

const AboutPage = () => {
  // Organization structured data
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "ToolsForge",
    "url": "https://toolsforgeapp.com",
    "logo": "https://toolsforgeapp.com/logo.png",
    "description": "ToolsForge is a collection of free online developer, text, SEO and productivity tools. All tools run in your browser with no signup required.",
    "foundingDate": "2024",
    "sameAs": [],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer support",
      "email": "support@toolsforgeapp.com"
    },
    "offers": {
      "@type": "Offer",
      "description": "Free online tools for developers, designers, and content creators",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  // WebSite structured data
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "ToolsForge",
    "url": "https://toolsforgeapp.com",
    "description": "Free online tools for developers, designers, marketers, and content creators",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://toolsforgeapp.com/all-tools?search={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  const features = [
    {
      icon: Zap,
      title: "Instant Results",
      description: "All tools work instantly in your browser. No waiting, no processing queues."
    },
    {
      icon: Shield,
      title: "Privacy First",
      description: "Your data never leaves your device. All processing happens locally in your browser."
    },
    {
      icon: Globe,
      title: "Works Everywhere",
      description: "Access our tools from any device - desktop, tablet, or mobile. No installation needed."
    },
    {
      icon: Heart,
      title: "100% Free",
      description: "All core tools are completely free. No signup required, no credit card needed."
    }
  ];

  const toolCategories = [
    { icon: Type, name: "Text Tools", description: "Word counter, case converter, Lorem Ipsum generator", href: "/category/text-tools" },
    { icon: Code2, name: "Developer Tools", description: "JSON formatter, regex tester, hash generator", href: "/category/developer-tools" },
    { icon: Search, name: "SEO Tools", description: "Meta tag generator, slug generator, URL encoder", href: "/category/seo-tools" },
    { icon: Sparkles, name: "Generators", description: "Password generator, UUID generator, placeholder images", href: "/category/generators" }
  ];

  return (
    <>
      <SEOHead
        title="About ToolsForge - Free Online Tools Platform"
        description="Learn about ToolsForge, a collection of free online developer, text, SEO and productivity tools."
        keywords="about toolsforge, free online tools, developer tools, text tools, seo tools, productivity tools"
        canonical="https://toolsforgeapp.com/about"
      />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }} />

      <div className="min-h-screen pt-24 pb-16 px-6" data-testid="about-page">
        <div className="max-w-4xl mx-auto">
          
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Hammer className="w-8 h-8 text-primary" />
            </div>
            <Badge variant="outline" className="mb-4 px-4 py-1.5 border-primary/30 text-primary">
              About Us
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 font-['Plus_Jakarta_Sans']">
              About ToolsForge
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Your trusted platform for free, privacy-focused online tools
            </p>
          </motion.div>

          {/* What is ToolsForge */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold mb-4 font-['Plus_Jakarta_Sans']">
              What is ToolsForge?
            </h2>
            <div className="prose prose-invert max-w-none">
              <p className="text-muted-foreground leading-relaxed mb-4">
                ToolsForge is a comprehensive collection of free online tools designed for developers, designers, content creators, and anyone who works with digital content. We provide essential utilities that help you work smarter and faster, all accessible directly from your web browser.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                From formatting JSON and generating secure passwords to converting text cases and testing regular expressions, ToolsForge offers over 180 carefully crafted tools that cover a wide range of everyday tasks. Each tool is designed with simplicity and efficiency in mind, allowing you to get results instantly without any learning curve.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Unlike many online tool platforms, ToolsForge prioritizes your privacy. All our tools process data locally in your browser - your information is never sent to our servers or stored anywhere. This means you can use our tools with confidence, even when working with sensitive data.
              </p>
            </div>
          </motion.section>

          {/* Our Mission */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mb-16 p-8 rounded-2xl bg-gradient-to-r from-primary/10 to-accent/10 border border-border"
          >
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold font-['Plus_Jakarta_Sans']">Our Mission</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Our mission is to democratize access to essential digital tools. We believe that everyone should have access to high-quality utilities without barriers - no signups, no subscriptions, no hidden fees.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We're committed to building tools that respect user privacy, work reliably, and save time. Every tool we create goes through careful consideration to ensure it solves a real problem in the simplest way possible.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              By keeping our tools free and accessible, we hope to support developers, students, small businesses, and creators around the world in their daily work.
            </p>
          </motion.section>

          {/* What You Can Do */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans']">
              What You Can Do on ToolsForge
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {toolCategories.map((category, index) => {
                const Icon = category.icon;
                return (
                  <Link
                    key={index}
                    to={category.href}
                    className="group p-5 rounded-xl border border-border bg-card hover:border-primary/50 hover:bg-card/80 transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1 group-hover:text-primary transition-colors">
                          {category.name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {category.description}
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="mt-6 text-center">
              <Link to="/all-tools">
                <Button variant="outline" className="gap-2">
                  Explore All 180+ Tools
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </motion.section>

          {/* Why Choose ToolsForge */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans']">
              Why Choose ToolsForge?
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="p-5 rounded-xl bg-muted/30 border border-border">
                    <Icon className="w-8 h-8 text-primary mb-3" />
                    <h3 className="font-semibold mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 p-6 rounded-xl bg-muted/30 border border-border">
              <h3 className="font-semibold mb-4">What Sets Us Apart</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">No account required - start using tools immediately</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">No ads interrupting your workflow</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">Clean, modern interface designed for productivity</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">Regular updates with new tools and improvements</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">Mobile-friendly - works on any device</span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Contact Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans']">
              Contact Us
            </h2>
            <div className="p-6 rounded-xl bg-muted/30 border border-border">
              <p className="text-muted-foreground mb-6">
                Have questions, suggestions, or feedback? We'd love to hear from you. Our team is always looking for ways to improve ToolsForge and add new tools that help our users.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="mailto:support@toolsforgeapp.com"
                  className="flex items-center gap-3 p-4 rounded-lg bg-background border border-border hover:border-primary/50 transition-colors"
                >
                  <Mail className="w-5 h-5 text-primary" />
                  <div>
                    <div className="font-medium">Email Us</div>
                    <div className="text-sm text-muted-foreground">support@toolsforgeapp.com</div>
                  </div>
                </a>
                <Link 
                  to="/blog"
                  className="flex items-center gap-3 p-4 rounded-lg bg-background border border-border hover:border-primary/50 transition-colors"
                >
                  <MessageSquare className="w-5 h-5 text-primary" />
                  <div>
                    <div className="font-medium">Read Our Blog</div>
                    <div className="text-sm text-muted-foreground">Tips, guides, and updates</div>
                  </div>
                </Link>
              </div>
            </div>
          </motion.section>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="text-center p-8 rounded-2xl bg-gradient-to-r from-primary/10 to-accent/10 border border-border"
          >
            <h2 className="text-2xl font-bold mb-3 font-['Plus_Jakarta_Sans']">
              Ready to Get Started?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              Explore our collection of free tools and see how ToolsForge can help streamline your workflow.
            </p>
            <div className="flex justify-center gap-4 flex-wrap">
              <Link to="/all-tools">
                <Button className="glow-blue">
                  Browse All Tools
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/compare/best-free-online-tools">
                <Button variant="outline">
                  See Our Top Picks
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default AboutPage;
